import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";
import { ArrowLeft, Plus, MessageSquare, Lock, Globe, Trash2, Send, ChevronDown, ChevronUp } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function VisibilityBadge({ visibility }) {
  if (visibility === "private") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#FEF3C7] text-[#92400E]" data-testid="comment-visibility-private">
        <Lock size={11} strokeWidth={2} /> Privado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold bg-[#DCFCE7] text-[#166534]" data-testid="comment-visibility-public">
      <Globe size={11} strokeWidth={2} /> Público
    </span>
  );
}

function CommentThread({ comment, onDeleted, onReplyPosted, currentUser }) {
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && replies.length === 0 && comment.replies_count > 0) {
      setLoadingReplies(true);
      try {
        const { data } = await api.get(`/inbox/comments/${comment.id}`);
        setReplies(data.replies || []);
      } catch (e) {
        toast.error("No se pudieron cargar las respuestas.");
      } finally {
        setLoadingReplies(false);
      }
    }
  };

  const submitReply = async () => {
    const t = replyText.trim();
    if (!t) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/inbox/comments/${comment.id}/reply`, { content: t });
      setReplies((r) => [...r, data]);
      setReplyText("");
      onReplyPosted && onReplyPosted(comment.id);
      toast.success("Respuesta publicada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al publicar la respuesta.");
    } finally {
      setPosting(false);
    }
  };

  const del = async () => {
    if (!window.confirm("¿Eliminar este comentario y todas sus respuestas? Esta acción no se puede deshacer.")) return;
    setDeleting(true);
    try {
      await api.delete(`/inbox/comments/${comment.id}`);
      toast.success("Comentario eliminado.");
      onDeleted && onDeleted(comment.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar.");
      setDeleting(false);
    }
  };

  const authorLabel = comment.author_alias || comment.author_name || "Anónimo";

  return (
    <article className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid={`comment-${comment.id}`}>
      <header className="flex items-start gap-3">
        <Avatar name={authorLabel} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#0A2540]" data-testid={`comment-author-${comment.id}`}>{authorLabel}</span>
            <span className="text-[11px] text-[#5C6A7D]" data-testid={`comment-date-${comment.id}`}>· {formatDate(comment.created_at)}</span>
            <VisibilityBadge visibility={comment.visibility} />
          </div>
          {comment.author_name && comment.author_alias && (
            <div className="text-[10px] text-[#94A3B8] mt-0.5">{comment.author_name}</div>
          )}
        </div>
        {comment.can_delete && (
          <button
            onClick={del}
            disabled={deleting}
            className="text-[#E11D48] hover:text-[#9F1239] p-1.5 rounded transition-colors disabled:opacity-40"
            title="Eliminar"
            data-testid={`delete-comment-${comment.id}`}
          >
            <Trash2 size={16} strokeWidth={1.75} />
          </button>
        )}
      </header>

      <div className="text-sm text-[#0A2540] whitespace-pre-wrap leading-relaxed pl-[52px]" data-testid={`comment-content-${comment.id}`}>
        {comment.content}
      </div>

      <div className="pl-[52px] flex items-center gap-3">
        <button
          onClick={toggle}
          className="text-xs text-[#2563EB] hover:text-[#1D4ED8] font-medium inline-flex items-center gap-1"
          data-testid={`toggle-replies-${comment.id}`}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {comment.replies_count > 0 ? `${comment.replies_count} respuesta${comment.replies_count === 1 ? "" : "s"}` : "Responder"}
        </button>
      </div>

      {expanded && (
        <div className="pl-[52px] space-y-3 border-l-2 border-[#E2E8F0] ml-[26px]" data-testid={`replies-panel-${comment.id}`}>
          {loadingReplies && <div className="text-xs text-[#5C6A7D] pl-4">Cargando respuestas…</div>}
          {replies.map((r) => {
            const rAuthor = r.author_alias || r.author_name || "Anónimo";
            return (
              <div key={r.id} className="pl-4 space-y-1" data-testid={`reply-${r.id}`}>
                <div className="flex items-center gap-2">
                  <Avatar name={rAuthor} size={28} />
                  <span className="text-xs font-semibold text-[#0A2540]">{rAuthor}</span>
                  <span className="text-[10px] text-[#5C6A7D]">· {formatDate(r.created_at)}</span>
                </div>
                <div className="text-xs text-[#0A2540] whitespace-pre-wrap leading-relaxed pl-[36px]" data-testid={`reply-content-${r.id}`}>{r.content}</div>
              </div>
            );
          })}
          {comment.can_reply && (
            <div className="pl-4 pt-2 space-y-2">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Escribe tu respuesta…"
                rows={2}
                className="rounded-sm border-[#E2E8F0] text-sm"
                data-testid={`reply-input-${comment.id}`}
              />
              <div className="flex justify-end">
                <Button
                  onClick={submitReply}
                  disabled={posting || !replyText.trim()}
                  size="sm"
                  className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] gap-1"
                  data-testid={`submit-reply-${comment.id}`}
                >
                  <Send size={13} /> {posting ? "Publicando…" : "Responder"}
                </Button>
              </div>
            </div>
          )}
          {!comment.can_reply && (
            <div className="pl-4 text-[10px] text-[#94A3B8] italic">Este hilo es privado; solo el autor y los administradores pueden participar.</div>
          )}
        </div>
      )}
    </article>
  );
}

export default function CommentBox() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState("all");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNew, setOpenNew] = useState(false);

  // New comment form state
  const [topicMode, setTopicMode] = useState("existing"); // existing | new
  const [topicPick, setTopicPick] = useState("");
  const [newTopicName, setNewTopicName] = useState("");
  const [content, setContent] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [acknowledged, setAcknowledged] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const loadTopics = async () => {
    try {
      const { data } = await api.get("/inbox/topics");
      setTopics(data);
    } catch (e) {
      toast.error("No se pudieron cargar los temas.");
    }
  };

  const loadComments = async (topicId = selectedTopicId) => {
    setLoading(true);
    try {
      const params = topicId && topicId !== "all" ? { topic_id: topicId } : {};
      const { data } = await api.get("/inbox/comments", { params });
      setComments(data);
    } catch (e) {
      toast.error("No se pudieron cargar los comentarios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTopics(); }, []);
  useEffect(() => { loadComments(selectedTopicId); /* eslint-disable-next-line */ }, [selectedTopicId]);

  const resetForm = () => {
    setTopicMode(topics.length > 0 ? "existing" : "new");
    setTopicPick(topics[0]?.id || "");
    setNewTopicName("");
    setContent("");
    setVisibility("public");
    setAcknowledged(false);
  };

  const openDialog = () => {
    resetForm();
    setOpenNew(true);
  };

  const publish = async () => {
    if (!acknowledged) {
      toast.error("Debes aceptar el aviso antes de publicar.");
      return;
    }
    if (!content.trim()) {
      toast.error("Escribe tu comentario.");
      return;
    }
    setPublishing(true);
    try {
      let topicId = topicPick;
      if (topicMode === "new") {
        const name = newTopicName.trim();
        if (!name) {
          toast.error("Escribe el nombre del nuevo tema.");
          setPublishing(false);
          return;
        }
        const { data: t } = await api.post("/inbox/topics", { name });
        topicId = t.id;
      }
      if (!topicId) {
        toast.error("Selecciona o crea un tema.");
        setPublishing(false);
        return;
      }
      await api.post("/inbox/comments", {
        topic_id: topicId,
        content: content.trim(),
        visibility,
        acknowledged,
      });
      toast.success("Comentario publicado.");
      setOpenNew(false);
      await loadTopics();
      await loadComments(topicId);
      setSelectedTopicId(topicId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al publicar.");
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleted = (id) => setComments((cs) => cs.filter((c) => c.id !== id));
  const handleReplyPosted = (parentId) =>
    setComments((cs) => cs.map((c) => (c.id === parentId ? { ...c, replies_count: (c.replies_count || 0) + 1 } : c)));

  const totalTopics = topics.length;
  const selectedTopicName = useMemo(
    () => topics.find((t) => t.id === selectedTopicId)?.name,
    [topics, selectedTopicId]
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="comment-box-page">
      <Link to="/mi-espacio" className="inline-flex items-center text-xs uppercase tracking-[0.18em] text-[#5C6A7D] hover:text-[#0A2540]" data-testid="back-to-mi-espacio">
        <ArrowLeft size={14} className="mr-1" /> Mi espacio
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B5CF6] font-bold flex items-center gap-1.5">
            <MessageSquare size={13} /> Buzón de comentarios
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">
            Ideas y sugerencias del equipo.
          </h1>
          <p className="mt-2 text-sm text-[#5C6A7D] max-w-2xl">
            Comparte propuestas por tema. Los comentarios públicos son visibles para toda tu compañía; los privados solo para ti y los administradores.
          </p>
        </div>
        <Button onClick={openDialog} className="rounded-sm bg-[#8B5CF6] hover:bg-[#7C3AED] gap-2" data-testid="new-comment-btn">
          <Plus size={16} /> Nuevo comentario
        </Button>
      </div>

      {/* Topic filter */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm p-4 flex items-center gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] text-[#5C6A7D] font-semibold">Filtrar por tema</div>
        <Select value={selectedTopicId} onValueChange={setSelectedTopicId}>
          <SelectTrigger className="rounded-sm h-9 border-[#E2E8F0] w-72" data-testid="topic-filter-trigger">
            <SelectValue placeholder="Todos los temas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="topic-opt-all">Todos los temas ({totalTopics})</SelectItem>
            {topics.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`topic-opt-${t.id}`}>
                {t.name} ({t.comments_count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedTopicName && (
          <span className="text-xs text-[#5C6A7D]">Mostrando: <strong className="text-[#0A2540]">{selectedTopicName}</strong></span>
        )}
      </div>

      {/* Comments list */}
      {loading ? (
        <div className="text-sm text-[#5C6A7D]">Cargando comentarios…</div>
      ) : comments.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-16 text-center text-sm text-[#5C6A7D]" data-testid="empty-comments">
          <MessageSquare className="mx-auto mb-3" size={28} strokeWidth={1.5} />
          Aún no hay comentarios en este espacio. ¡Sé el primero en compartir una idea!
        </div>
      ) : (
        <div className="space-y-3" data-testid="comments-list">
          {comments.map((c) => (
            <CommentThread
              key={c.id}
              comment={c}
              onDeleted={handleDeleted}
              onReplyPosted={handleReplyPosted}
              currentUser={user}
            />
          ))}
        </div>
      )}

      {/* New comment dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg" data-testid="new-comment-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo comentario</DialogTitle>
            <DialogDescription>Elige un tema y comparte tu idea o sugerencia con el equipo.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Topic selection */}
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-[0.15em] text-[#5C6A7D] font-semibold">Tema</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTopicMode("existing")}
                  className={`flex-1 rounded-sm border py-2 text-sm font-medium transition-colors ${topicMode === "existing" ? "border-[#8B5CF6] bg-[#F5F3FF] text-[#8B5CF6]" : "border-[#E2E8F0] text-[#5C6A7D]"}`}
                  data-testid="topic-mode-existing"
                >
                  Tema existente
                </button>
                <button
                  type="button"
                  onClick={() => setTopicMode("new")}
                  className={`flex-1 rounded-sm border py-2 text-sm font-medium transition-colors ${topicMode === "new" ? "border-[#8B5CF6] bg-[#F5F3FF] text-[#8B5CF6]" : "border-[#E2E8F0] text-[#5C6A7D]"}`}
                  data-testid="topic-mode-new"
                >
                  Nuevo tema
                </button>
              </div>
              {topicMode === "existing" ? (
                <Select value={topicPick} onValueChange={setTopicPick}>
                  <SelectTrigger className="rounded-sm h-10 border-[#E2E8F0]" data-testid="dialog-topic-select">
                    <SelectValue placeholder={topics.length > 0 ? "Selecciona un tema" : "No hay temas todavía"} />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`dialog-topic-opt-${t.id}`}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={newTopicName}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  placeholder="Nombre del nuevo tema (p. ej. Herramientas de obra)"
                  className="rounded-sm h-10 border-[#E2E8F0]"
                  maxLength={120}
                  data-testid="new-topic-input"
                />
              )}
            </div>

            {/* Content */}
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-[0.15em] text-[#5C6A7D] font-semibold">Tu comentario</div>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Comparte tu idea, sugerencia o comentario…"
                rows={5}
                maxLength={2000}
                className="rounded-sm border-[#E2E8F0] text-sm"
                data-testid="new-comment-content"
              />
              <div className="text-[10px] text-[#94A3B8] text-right">{content.length} / 2000</div>
            </div>

            {/* Visibility */}
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-[0.15em] text-[#5C6A7D] font-semibold">Visibilidad</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setVisibility("public")}
                  className={`flex-1 rounded-sm border py-3 px-4 text-left transition-colors ${visibility === "public" ? "border-[#16A34A] bg-[#DCFCE7]" : "border-[#E2E8F0]"}`}
                  data-testid="visibility-public-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Globe size={14} className="text-[#16A34A]" />
                    <span className="font-semibold text-sm text-[#0A2540]">Público</span>
                  </div>
                  <div className="text-[11px] text-[#5C6A7D]">Todo el equipo lo verá y podrá responder.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility("private")}
                  className={`flex-1 rounded-sm border py-3 px-4 text-left transition-colors ${visibility === "private" ? "border-[#F59E0B] bg-[#FEF3C7]" : "border-[#E2E8F0]"}`}
                  data-testid="visibility-private-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Lock size={14} className="text-[#B45309]" />
                    <span className="font-semibold text-sm text-[#0A2540]">Privado</span>
                  </div>
                  <div className="text-[11px] text-[#5C6A7D]">Solo tú y los administradores lo verán.</div>
                </button>
              </div>
            </div>

            {/* Mandatory acknowledgment */}
            <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-sm p-3 flex gap-3 items-start" data-testid="acknowledge-notice">
              <Checkbox
                checked={acknowledged}
                onCheckedChange={(v) => setAcknowledged(!!v)}
                id="ack-cb"
                data-testid="acknowledge-checkbox"
                className="mt-0.5"
              />
              <label htmlFor="ack-cb" className="text-xs text-[#4C1D95] leading-relaxed cursor-pointer">
                <strong>Aviso:</strong> Tus comentarios están dirigidos al administrador de la aplicación y este espacio es para compartir <em>ideas colaborativas</em>. Al continuar aceptas esta condición.
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)} className="rounded-sm" data-testid="cancel-new-comment">
              Cancelar
            </Button>
            <Button
              onClick={publish}
              disabled={publishing || !acknowledged || !content.trim()}
              className="rounded-sm bg-[#8B5CF6] hover:bg-[#7C3AED]"
              data-testid="publish-comment-btn"
            >
              {publishing ? "Publicando…" : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
